import React, { useMemo, useState } from "react";
import { Loader2, Search, X } from "lucide-react";

interface MccMappingsModalContentProps {
  loading: boolean;
  error?: string | null;
  purposeCodes: Array<{ purposeCode: string; useCase: string }>;
}

const MccMappingsModalContent: React.FC<MccMappingsModalContentProps> = ({
  loading,
  error,
  purposeCodes,
}) => {
  const [search, setSearch] = useState("");

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return purposeCodes;
    return purposeCodes.filter(
      (row) =>
        row.purposeCode.toLowerCase().includes(term) ||
        row.useCase.toLowerCase().includes(term),
    );
  }, [purposeCodes, search]);

  return (
    <div className="w-full overflow-y-auto max-h-[70vh]">
      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading mappings...
        </div>
      ) : error ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {error}
        </div>
      ) : (
        <div className="px-1 sm:px-2 py-1">
          <div className="relative mb-4">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter purpose code or use case"
              className="w-full pl-9 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none"
            />
            {!!search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear filter"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="text-xs text-gray-500 mb-4">
            Showing {filteredRows.length} of {purposeCodes.length} purpose code mapping(s)
          </div>
          <div className="max-h-[360px] overflow-y-auto overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-left border-b border-gray-200 text-gray-600">
                  <th className="py-3.5 px-4 font-medium">Purpose Code</th>
                  <th className="py-3.5 pr-4 font-medium">Use Case</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.purposeCode} className="border-b border-gray-100">
                    <td className="py-3.5 px-4 font-medium text-gray-800">{row.purposeCode}</td>
                    <td className="py-3.5 pr-4 text-gray-700">{row.useCase}</td>
                  </tr>
                ))}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan={2} className="py-8 text-center text-gray-500">
                      {search ? "No purpose code mappings match your filter" : "No purpose code mappings found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MccMappingsModalContent;
